import { FC, useState } from 'react';
import { Box, Typography, Divider, Grid, Fade, FormControlLabel, Checkbox } from '@mui/material';
import { CheckCircle, CircleOutlined } from '@mui/icons-material';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import { setRoutingOptions } from '@acorn-portal/shared/src/store/midTermAdjustmentRatingResponse.slice';
import Price from '@acorn-portal/shared/src/components/display/price';
import Endorsements from '@acorn-portal/shared/src/components/display/endorsements';
import DriverExcess from '@acorn-portal/shared/src/components/display/driver-excess';
import MileageInfoPanel from '@acorn-portal/shared/src/components/copy/MileageInfoPanel';
import { PricingGuidanceAlert } from '@acorn-portal/shared/src/pages/mta/components/pricing-guidance-alert';
import { findPaymentOptionHasOneOfTypes, hasPaymentOptionOfType } from '@acorn-portal/shared/src/pages/mta/utils';
import { useNavigate } from 'react-router';
import SummaryPrice from '@acorn-portal/shared/src/components/display/summary-price';
import { isTelematics } from '@acorn-portal/shared/src/utils';
import { PaymentType } from '../../utils/types/payment';

const TopUpMilesPrice: FC = () => {
	const [confirmChanges, setConfirmChanges] = useState(false);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	// Pull in the same state from Redux as used in MtaSummaryScreen
	const state = useAppSelector((redux) => redux.midTermAdjustment);
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const mtaResponse = useAppSelector((redux) => redux.midTermAdjustmentResponse);

	// Handle navigation errors
	if (mtaResponse.paymentOptions.length === 0) {
		navigate(ROUTE_PATHS.error);
	}

	// Use the same pricing logic as MtaSummaryScreen
	let price = findPaymentOptionHasOneOfTypes(mtaResponse.paymentOptions, [
		PaymentType.SINGLE_PAYMENT_PAYMENT_CARD_AP,
		PaymentType.SINGLE_PAYMENT_PAYMENT_CARD_RP,
		PaymentType.INSTALMENT_ADJUSTMENT_SPREAD_EVENLY,
		PaymentType.INSTALMENT_ADJUSTMENT_NEXT_INSTALMENT,
		PaymentType.SINGLE_PAYMENT_DIRECT_CREDIT_RP,
	]);

	// Handle return premium case
	if (price?.totalAmountPayable && price?.totalAmountPayable < 0) {
		const financePrice = findPaymentOptionHasOneOfTypes(mtaResponse.paymentOptions, [
			PaymentType.INSTALMENT_ADJUSTMENT_SPREAD_EVENLY,
			PaymentType.INSTALMENT_ADJUSTMENT_NEXT_INSTALMENT,
		]);

		if (financePrice) {
			price = financePrice;
		}
	}

	const handleRoutingToPaymentOptions = () => {
		let isReturnPremium = false;
		let isCardPaymentAvailable = false;
		let isFinanceAvailable = false;

		if (price?.totalAmountPayable && price?.totalAmountPayable < 0) {
			isReturnPremium = true;
			isCardPaymentAvailable = hasPaymentOptionOfType(mtaResponse.paymentOptions, [
				PaymentType.SINGLE_PAYMENT_PAYMENT_CARD_RP,
			]);
		} else {
			isCardPaymentAvailable = hasPaymentOptionOfType(mtaResponse.paymentOptions, [
				PaymentType.SINGLE_PAYMENT_PAYMENT_CARD_AP,
			]);
		}
		isFinanceAvailable = hasPaymentOptionOfType(mtaResponse.paymentOptions, [
			PaymentType.INSTALMENT_ADJUSTMENT_SPREAD_EVENLY,
			PaymentType.INSTALMENT_ADJUSTMENT_NEXT_INSTALMENT,
		]);

		dispatch(
			setRoutingOptions({
				isReturnPremium,
				isCardPaymentAvailable,
				isFinanceAvailable,
			}),
		);

		navigate(ROUTE_PATHS.makeAChangePaymentOptions);
	};

	return (
		<JourneyCardLayout pageTitle='Price Summary' loading={false}>
			<Fade in timeout={400}>
				<Box maxWidth='md' mx='auto' sx={{ mb: 3 }}>
					<Box sx={{ mb: 3 }}>
						<Typography variant='h5' component='h2' color='primary.main' gutterBottom>
							Your Miles Top-Up Summary
						</Typography>

						<Typography variant='body1'>
							We&apos;ve calculated the price to increase your annual mileage. Please review the details below
							carefully.
						</Typography>

						{isTelematics(fullPolicy.product) && <PricingGuidanceAlert sx={{ mt: 2 }} />}
					</Box>

					<Box sx={{ mb: 4 }}>
						<SummaryPrice
							mtaStartDate={state.mtaStartDate}
							totalAmountPayable={price?.totalAmountPayable}
							fee={mtaResponse.coverDetails.fee}
							product={fullPolicy.product}
						/>
					</Box>

					<MileageInfoPanel
						currentMileage={fullPolicy.vehicleDetails.annualMileage}
						newMileage={state.vehicleDetails.annualMileage}
					/>

					{state.vehicleDetails.coverDefinitionCode !== 'TPO' && (
						<>
							<Box sx={{ width: '100%', py: 2 }}>
								<Typography variant='h6' gutterBottom>
									Policy Excess
								</Typography>
								<Divider sx={{ mb: 2 }} />

								<Grid container alignItems='center' sx={{ py: 2 }} gap={2}>
									<Grid flex={1}>
										<Typography>
											{!!mtaResponse.coverDetails.voluntaryExcess && (
												<Price value={mtaResponse.coverDetails.voluntaryExcess} description='Voluntary Excess' />
											)}
										</Typography>
									</Grid>
									<Grid>
										<Price value={mtaResponse.coverDetails.compulsoryExcess} description='Compulsory Excess' />
									</Grid>
								</Grid>
							</Box>

							<DriverExcess drivers={mtaResponse.drivers} driverExcesses={mtaResponse.coverDetails.driverExcesses} />
							<Divider sx={{ mb: 4 }} />
						</>
					)}

					<Grid>
						<Typography variant='h6' gutterBottom>
							Key Endorsements
						</Typography>
						<Divider sx={{ mb: 2 }} />
						<Endorsements endorsements={mtaResponse.coverDetails.endorsements || []} />
					</Grid>

					<Box sx={{ mt: 4 }}>
						<Divider sx={{ mb: 3 }} />

						<Typography variant='body2'>
							Please be aware that by changing your annual mileage, there may be updates to your policy terms, such as
							endorsements and excesses. These changes are reflected above and will be detailed in your updated policy
							documents.
						</Typography>

						<FormControlLabel
							control={
								<Checkbox
									id='confirm'
									name='confirm'
									slotProps={{
										input: {
											'aria-label': 'Confirm checkbox',
										},
									}}
									icon={<CircleOutlined />}
									checkedIcon={<CheckCircle />}
									sx={{ '& .MuiSvgIcon-root': { fontSize: '3.5rem' } }}
									value={confirmChanges}
									onChange={() => setConfirmChanges(!confirmChanges)}
								/>
							}
							labelPlacement='end'
							label={
								<Typography variant='body2' fontWeight='medium'>
									I confirm all the details above are correct and I want to proceed with this change.
								</Typography>
							}
						/>
					</Box>

					<ActionButtons>
						<BackButton onClick={() => navigate(ROUTE_PATHS.makeAChangeMileage)}>Back</BackButton>
						<ContinueButton onClick={handleRoutingToPaymentOptions} disabled={!confirmChanges}>
							To Payment
						</ContinueButton>
					</ActionButtons>
				</Box>
			</Fade>
		</JourneyCardLayout>
	);
};

export default TopUpMilesPrice;
